import { useTranslation } from 'react-i18next';

import type { ViewMode } from '@/catalog/useCatalogState';

interface ToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  categories: string[];
  category: string | null;
  onCategoryChange: (v: string | null) => void;
  subcategories: string[];
  subcategory: string | null;
  onSubcategoryChange: (v: string | null) => void;
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  resultCount: number;
}

const SELECT_CLASS =
  'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-appsol-500 focus:outline-none focus:ring-1 focus:ring-appsol-500';

export function CatalogToolbar({
  search,
  onSearchChange,
  categories,
  category,
  onCategoryChange,
  subcategories,
  subcategory,
  onSubcategoryChange,
  view,
  onViewChange,
  resultCount,
}: ToolbarProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="min-w-[12rem] flex-1">
        <label htmlFor="catalog-search" className="sr-only">
          {t('home.toolbar.searchLabel')}
        </label>
        <input
          id="catalog-search"
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('home.toolbar.searchPlaceholder')}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-appsol-500 focus:outline-none focus:ring-1 focus:ring-appsol-500"
        />
      </div>

      {categories.length > 0 && (
        <div>
          <label htmlFor="catalog-category" className="sr-only">
            {t('home.toolbar.categoryLabel')}
          </label>
          <select
            id="catalog-category"
            value={category ?? ''}
            onChange={(e) => onCategoryChange(e.target.value || null)}
            className={SELECT_CLASS}
          >
            <option value="">{t('home.toolbar.allCategories')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      )}

      {subcategories.length > 0 && (
        <div>
          <label htmlFor="catalog-subcategory" className="sr-only">
            {t('home.toolbar.subcategoryLabel')}
          </label>
          <select
            id="catalog-subcategory"
            value={subcategory ?? ''}
            onChange={(e) => onSubcategoryChange(e.target.value || null)}
            className={SELECT_CLASS}
          >
            <option value="">{t('home.toolbar.allSubcategories')}</option>
            {subcategories.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center gap-3">
        <p aria-live="polite" className="text-xs text-slate-500">
          {t('home.toolbar.count', { count: resultCount })}
        </p>

        <div role="group" aria-label={t('home.toolbar.viewLabel')} className="inline-flex rounded-md border border-slate-300 bg-white shadow-sm">
          <button
            type="button"
            aria-pressed={view === 'cards'}
            onClick={() => onViewChange('cards')}
            className={`rounded-l-md px-3 py-1.5 text-xs font-medium transition ${
              view === 'cards' ? 'bg-appsol-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t('home.toolbar.viewCards')}
          </button>
          <button
            type="button"
            aria-pressed={view === 'list'}
            onClick={() => onViewChange('list')}
            className={`rounded-r-md border-l border-slate-300 px-3 py-1.5 text-xs font-medium transition ${
              view === 'list' ? 'bg-appsol-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t('home.toolbar.viewList')}
          </button>
        </div>
      </div>
    </div>
  );
}
